import {
  Card,
  Col,
  Input,
  Row,
  Tag,
  Table,
  Badge,
  Space,
  Typography,
} from "antd";
import { NEW_BASE_URL, API_BASE_URL } from "../api";
import moment from "moment";
import { useEffect, useState } from "react";
import { useApi, useMount } from "../hooks";
import { ISchedule, TStatic, IVideo } from "../interfaces";
import AppHeader from "../layouts/header";
import {
  VideoCameraOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  PlayCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  BarChartOutlined,
  ExclamationCircleOutlined,
  YoutubeFilled,
  PauseCircleOutlined,
} from "@ant-design/icons";
import { statusColor } from "../utils/constants";
import { ColumnsType } from "antd/es/table";
import { useNavigate } from "@tanstack/react-router";
import YoutubeStats from "./youtubeStats";

const { Title, Text } = Typography;

export default function LandingPage() {
  const [logStreamKey, setLogStreamKey] = useState("");
  const [length, setLength] = useState(0);
  const { data, load } = useApi<TStatic>(NEW_BASE_URL + "videos/statistic");
  const { data: schedules, load: getSchedule } = useApi<ISchedule[]>(
    API_BASE_URL + `schedules`
  );
  const { data: failedVideos, load: loadFailedVideos } = useApi<{
    videos: IVideo[];
  }>(NEW_BASE_URL + "videos?status=failed&pageSize=10");

  const navigate = useNavigate();

  useMount(() => {
    load();
    getSchedule();
    loadFailedVideos();
  });

  useEffect(() => {
    const currentDate = new Date().toISOString().split("T")[0];
    const number = schedules?.filter((item) => {
      return moment(item.published_at).format("YYYY-MM-DD") === currentDate;
    });
    setLength(number?.length || 0);
  }, [schedules]);

  const { overall, today } = data || {};
  const todayData = moment().format("YYYY-MM-DD");

  const StatCard = ({
    title,
    value = 0,
    icon,
    color,
    url,
  }: {
    title: string;
    value?: number;
    icon: React.ReactNode;
    color: string;
    url?: any;
  }) => {
    return (
      <Col
        xs={24}
        sm={12}
        md={8}
        lg={6}
        xl={6}
        xxl={4.8}
        style={{ cursor: "pointer" }}
      >
        <Card
          style={{
            borderRadius: 14,
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 20 }}
          onClick={() => {
            navigate({ to: url });
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                background: `linear-gradient(135deg, ${color}18 0%, ${color}35 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: color,
                fontSize: 24,
                flexShrink: 0,
              }}
            >
              {icon}
            </div>
            <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <Text
                style={{
                  fontSize: 12,
                  color: "#6b7280",
                  marginBottom: 2,
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </Text>
              <Title
                level={4}
                style={{
                  margin: 0,
                  color: "#1f2937",
                  fontSize: 22,
                  whiteSpace: "nowrap",
                }}
              >
                {value}
              </Title>
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  const failedColumns: ColumnsType<IVideo> = [
    {
      title: "Video ID",
      dataIndex: "vid",
      key: "vid",
      width: 100,
      render: (vid) => <Text strong>#{vid}</Text>,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (title) => <Text style={{ color: "#374151" }}>{title}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center",
      render: (status) => (
        <Tag color={statusColor[status]} style={{ fontWeight: 500 }}>
          <Space size={4}>
            <CloseCircleOutlined />
            {status}
          </Space>
        </Tag>
      ),
    },
    {
      title: "Stream Key",
      dataIndex: "stream_key",
      key: "stream_key",
      width: 180,
      render: (key) => (
        <Text code style={{ fontSize: 12 }}>
          {key}
        </Text>
      ),
    },
    {
      title: "Scheduled",
      dataIndex: "schedule_at",
      key: "schedule_at",
      width: 150,
      render: (date) => moment(date).format("DD MMM YYYY"),
    },
  ];

  return (
    <div
      style={{
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
        paddingBottom: 40,
      }}
    >
      <AppHeader />
      <div style={{ padding: "24px 32px" }}>
        {/* Header */}
        <div
          style={{
            marginBottom: 32,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <Title
              level={2}
              style={{
                margin: 0,
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <BarChartOutlined style={{ color: "#0ea5e9" }} />
              Dashboard Overview
            </Title>
            <Text style={{ color: "#6b7280", fontSize: 16 }}>
              Monitor your video streaming metrics and failed jobs
            </Text>
          </div>
          <Input.Search
            allowClear
            placeholder="Search logs by stream key"
            enterButton="View Logs"
            style={{ minWidth: 320, maxWidth: 420 }}
            value={logStreamKey}
            onChange={(event) => setLogStreamKey(event.target.value)}
            onSearch={(value) =>
              navigate({
                to: "/bulk-api",
                search: value.trim() ? { stream_key: value.trim() } : {},
              })
            }
          />
        </div>

        {/* Today's Stats */}
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 16,
            border: "none",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          }}
          headStyle={{
            borderBottom: "1px solid #f3f4f6",
            padding: "20px 24px",
          }}
          title={
            <Space>
              <CalendarOutlined style={{ color: "#0ea5e9" }} />
              <span style={{ fontSize: 18, fontWeight: 600, color: "#374151" }}>
                Today's Statistics
              </span>
            </Space>
          }
        >
          <Row gutter={[24, 24]}>
            <StatCard
              title="Total Schedules"
              value={length}
              icon={<CalendarOutlined />}
              color="#8b5cf6"
              url={`/schedules`}
            />
            <StatCard
              title="Active Live"
              value={today?.totalLive}
              icon={<PlayCircleOutlined />}
              color="#10b981"
              url={`/list?status=live&date=${todayData}`}
            />
            <StatCard
              title="Rendering"
              value={today?.totalRendering}
              icon={<SyncOutlined spin={Boolean(today?.totalRendering && today.totalRendering > 0)} />}
              color="#f59e0b"
              url={`/list?status=rendering&date=${todayData}`}
            />
            <StatCard
              title="Rendered"
              value={today?.totalRendered}
              icon={<CheckCircleOutlined />}
              color="#0ea5e9"
              url={`/list?status=rendered&date=${todayData}`}
            />
            <StatCard
              title="Failed"
              value={today?.totalFailed}
              icon={<ExclamationCircleOutlined />}
              color="#ef4444"
              url={`/list?status=failed&date=${todayData}`}
            />
            <StatCard
              title="In Queue"
              value={today?.totalInQueue}
              icon={<SyncOutlined />}
              color="#f97316"
              url={`/list?status=in-queue&date=${todayData}`}
            />
            <StatCard
              title="Halted"
              value={today?.totalHalted}
              icon={<PauseCircleOutlined />}
              color="#0f766e"
              url={`/list?status=halted&date=${todayData}`}
            />
          </Row>
        </Card>

        {/* Overall Stats */}
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 16,
            border: "none",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          }}
          headStyle={{
            borderBottom: "1px solid #f3f4f6",
            padding: "20px 24px",
          }}
          title={
            <Space>
              <BarChartOutlined style={{ color: "#0ea5e9" }} />
              <span style={{ fontSize: 18, fontWeight: 600, color: "#374151" }}>
                Overall Statistics
              </span>
            </Space>
          }
        >
          <Row gutter={[24, 24]}>
            <StatCard
              title="Total Videos"
              value={overall?.total}
              icon={<VideoCameraOutlined />}
              color="#0ea5e9"
              url={`/list`}
            />
            <StatCard
              title="Active Live"
              value={overall?.totalLive}
              icon={<PlayCircleOutlined />}
              color="#10b981"
              url={`/list?status=live`}
            />
            <StatCard
              title="Rendering"
              value={overall?.totalRendering}
              icon={<SyncOutlined spin={Boolean(overall?.totalRendering && overall.totalRendering > 0)} />}
              color="#f59e0b"
              url={`/list?status=rendering`}
            />
            <StatCard
              title="Rendered"
              value={overall?.totalRendered}
              icon={<CheckCircleOutlined />}
              color="#0ea5e9"
              url={`/list?status=rendered`}
            />
            <StatCard
              title="Failed"
              value={overall?.totalFailed}
              icon={<ExclamationCircleOutlined />}
              color="#ef4444"
              url={`/list?status=failed`}
            />
            <StatCard
              title="In Queue"
              value={overall?.totalInQueue}
              icon={<SyncOutlined />}
              color="#f97316"
              url={`/list?status=in-queue`}
            />
            <StatCard
              title="Halted"
              value={overall?.totalHalted}
              icon={<PauseCircleOutlined />}
              color="#0f766e"
              url={`/list?status=in-queue`}
            />
          </Row>
        </Card>

        <Card
          title={
            <Space>
              <YoutubeFilled style={{ color: "red", fontSize: "1.5rem" }} />
              <span style={{ fontSize: 18, fontWeight: 600, color: "#374151" }}>
                Youtube's Statistics
              </span>
            </Space>
          }
        >
          <YoutubeStats stats={data?.youtube} length={length} />
        </Card>

        {/* Failed Videos Table */}
        <Card
          style={{
            borderRadius: 16,
            border: "none",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            marginTop: "24px",
          }}
          headStyle={{
            borderBottom: "1px solid #f3f4f6",
            padding: "20px 24px",
          }}
          title={
            <Space>
              <CloseCircleOutlined style={{ color: "#ef4444" }} />
              <span style={{ fontSize: 18, fontWeight: 600, color: "#374151" }}>
                Failed Videos
              </span>
              <Badge
                count={failedVideos?.videos?.length || 0}
                style={{ backgroundColor: "#ef4444" }}
              />
            </Space>
          }
        >
          <Table
            columns={failedColumns}
            dataSource={failedVideos?.videos || []}
            rowKey="_id"
            pagination={false}
            size="middle"
            locale={{ emptyText: "No failed videos" }}
            style={{ borderRadius: 12 }}
            scroll={{ x: 500, y: 500 }}
          />
        </Card>
      </div>
    </div>
  );
}
